import { getFrontHoles, getBackHoles } from '@/lib/golfEngine';

// ─── Normalized Input Types ───────────────────────────────────────────────────

export type RIPlayer = { id: string; name: string; strokesReceived: number };
export type RITeam = { players: RIPlayer[] };
export type RIScore = {
  playerId: string;
  holeNumber: number;
  grossScore: number | null;
  netScore: number | null;
  receivesStroke: boolean;
};
export type RIHole = { number: number; par: number | null };
export type RIPress = { startingHole: number; stackCount: number };

export type RIInput = {
  teams: [RITeam, RITeam];
  scores: RIScore[];
  holes: RIHole[];
  presses: RIPress[];
  matchFormat: '1v1' | '2v2';
  startingHole: number;
  stakes: { front: number; back: number; overall: number };
  totalAmountForA: number;
  nassauAmountForA?: number | null;
  pressAmountForA?: number | null;
};

// ─── Output Type ──────────────────────────────────────────────────────────────

export type RICard = {
  type: 'hole_in_one' | 'gross_heroics' | 'press_moment' | 'handicap_injustice' | 'stroke_pressure' | 'crash_and_burn' | 'press_intelligence' | 'teammate_dynamic';
  priority: number;
  text: string;
  holeNumber: number | null;
  subjectName: string | null;
  opponentName: string | null;
  teamName: string | null;
  dataPoints: string[];
  margin: number;
  stackCount?: number;
  pressTotal?: number;
  nassauTotal?: number;
  carrierName?: string;
  partnerName?: string;
  carrierHoleWins?: number;
  partnerHoleWins?: number;
};

// ─── Internal Helpers ─────────────────────────────────────────────────────────

function buildHoleResults(input: RIInput): Record<number, 'A' | 'B' | 'Tie'> {
  const result: Record<number, 'A' | 'B' | 'Tie'> = {};
  const teamAIds = new Set(input.teams[0].players.map(p => p.id));
  const teamBIds = new Set(input.teams[1].players.map(p => p.id));
  const allHoleNums = [...new Set(input.scores.map(s => s.holeNumber))];
  for (const holeNum of allHoleNums) {
    const aNets = input.scores
      .filter(s => teamAIds.has(s.playerId) && s.holeNumber === holeNum && s.netScore !== null)
      .map(s => s.netScore as number);
    const bNets = input.scores
      .filter(s => teamBIds.has(s.playerId) && s.holeNumber === holeNum && s.netScore !== null)
      .map(s => s.netScore as number);
    if (aNets.length === 0 || bNets.length === 0) continue;
    const aBest = Math.min(...aNets);
    const bBest = Math.min(...bNets);
    if (aBest < bBest) result[holeNum] = 'A';
    else if (bBest < aBest) result[holeNum] = 'B';
    else result[holeNum] = 'Tie';
  }
  return result;
}

function getRunningStatus(input: RIInput, startHole: number, endHole: number): Map<number, number> {
  const map = new Map<number, number>();
  const holeResults = buildHoleResults(input);
  let status = 0;
  map.set(startHole - 1, 0);
  for (let h = startHole; h <= endHole; h++) {
    const r = holeResults[h];
    if (r === 'A') status++;
    else if (r === 'B') status--;
    map.set(h, status);
  }
  return map;
}

// ─── Main Function ────────────────────────────────────────────────────────────

export function generateRoundIntelligence(input: RIInput, maxCards: number): RICard[] {
  try {
    const allPlayers = input.teams.flatMap(t => t.players);
    const teamAPlayerIds = new Set(input.teams[0]?.players.map(p => p.id) ?? []);
    const teamBPlayerIds = new Set(input.teams[1]?.players.map(p => p.id) ?? []);
    const frontHoles = getFrontHoles(input.startingHole as 1 | 10);
    const backHoles = getBackHoles(input.startingHole as 1 | 10);
    const allHoles = [...frontHoles, ...backHoles];

    const completeHoles = allHoles.filter(holeNum =>
      allPlayers.every(pl => {
        const s = input.scores.find(sc => sc.playerId === pl.id && sc.holeNumber === holeNum);
        return s && s.grossScore !== null && s.netScore !== null;
      })
    );
    if (completeHoles.length < 6) return [];

    const candidates: RICard[] = [];

    // ─── Crash and Burn (priority 1) ──────────────────────────────────────────
    {
      type CabCandidate = {
        holeNumber: number;
        grossScore: number;
        holePar: number | null;
        parIsValid: boolean;
        subjectName: string;
        opponentName: string;
        pressRunning: boolean;
        nassauLeg: boolean;
        statusSwing: number;
        maxStackCount: number;
      };

      let cabHoleResultMap: Record<number, 'A' | 'B' | 'Tie'> = {};
      let cabMapBuilt = false;
      try {
        cabHoleResultMap = buildHoleResults(input);
        cabMapBuilt = Object.keys(cabHoleResultMap).length > 0;
      } catch {
        cabMapBuilt = false;
      }

      if (cabMapBuilt) {
        const frontHoleSet = new Set(getFrontHoles(input.startingHole as 1 | 10));
        const backHoleSet = new Set(getBackHoles(input.startingHole as 1 | 10));

        let frontStatusMap: Map<number, number> = new Map();
        let backStatusMap: Map<number, number> = new Map();
        try {
          const frontArr = Array.from(frontHoleSet);
          if (frontArr.length > 0) {
            frontStatusMap = getRunningStatus(input, Math.min(...frontArr), Math.max(...frontArr));
          }
        } catch { frontStatusMap = new Map(); }
        try {
          const backArr = Array.from(backHoleSet);
          if (backArr.length > 0) {
            backStatusMap = getRunningStatus(input, Math.min(...backArr), Math.max(...backArr));
          }
        } catch { backStatusMap = new Map(); }

        const cabCandidates: CabCandidate[] = [];

        for (const holeNum of completeHoles) {
          if (holeNum < 15) continue;

          const holeResult = cabHoleResultMap[holeNum];
          if (!holeResult || holeResult === 'Tie') continue;

          const losingIsA = holeResult === 'B';
          const losingIds = losingIsA ? Array.from(teamAPlayerIds) : Array.from(teamBPlayerIds);
          const winningIds = losingIsA ? Array.from(teamBPlayerIds) : Array.from(teamAPlayerIds);

          const losingEntries = losingIds.map(pid => {
            const sc = input.scores.find(s => s.playerId === pid && s.holeNumber === holeNum);
            const pl = allPlayers.find(p => p.id === pid);
            return { player: pl ?? null, gross: sc?.grossScore ?? null };
          }).filter((e): e is { player: RIPlayer; gross: number } =>
            e.player !== null && e.gross !== null
          );
          if (losingEntries.length === 0) continue;

          const worstGross = Math.max(...losingEntries.map(e => e.gross));
          const worstGroup = losingEntries.filter(e => e.gross === worstGross);
          let subjectEntry = worstGroup[0];
          if (worstGroup.length > 1) {
            const maxSR = Math.max(...worstGroup.map(e => e.player.strokesReceived));
            subjectEntry = worstGroup.find(e => e.player.strokesReceived === maxSR) ?? worstGroup[0];
          }

          const holePar = input.holes.find(h => h.number === holeNum)?.par ?? null;
          const parIsValid = holePar !== null && holePar >= 3 && holePar <= 5;
          const badScore = parIsValid && holePar !== null
            ? worstGross >= holePar + 2
            : worstGross >= 7;
          if (!badScore) continue;

          const runningPresses = input.presses.filter(p => p.startingHole <= holeNum);
          const pressRunning = runningPresses.length > 0;
          const maxStackCount = pressRunning ? Math.max(...runningPresses.map(p => p.stackCount)) : 0;
          const nassauLeg = holeNum === 9 || holeNum === 18;

          let statusSwing = 0;
          try {
            const legMap = frontHoleSet.has(holeNum) ? frontStatusMap : backStatusMap;
            const before = legMap.get(holeNum - 1);
            const after = legMap.get(holeNum);
            if (before !== undefined && after !== undefined) {
              statusSwing = Math.abs(after - before);
            }
          } catch { statusSwing = 0; }

          if (!pressRunning && !nassauLeg && statusSwing < 2) continue;

          const winningPlayers = winningIds
            .map(pid => allPlayers.find(p => p.id === pid))
            .filter((p): p is RIPlayer => p !== undefined);
          const opponentName = winningPlayers.map(p => p.name).join(' & ');

          cabCandidates.push({
            holeNumber: holeNum,
            grossScore: worstGross,
            holePar,
            parIsValid,
            subjectName: subjectEntry.player.name,
            opponentName,
            pressRunning,
            nassauLeg,
            statusSwing,
            maxStackCount,
          });
        }

        if (cabCandidates.length > 0) {
          cabCandidates.sort((a, b) => {
            const aClose = a.nassauLeg ? 1 : 0;
            const bClose = b.nassauLeg ? 1 : 0;
            if (bClose !== aClose) return bClose - aClose;
            const aCombo = a.pressRunning && a.nassauLeg ? 1 : 0;
            const bCombo = b.pressRunning && b.nassauLeg ? 1 : 0;
            if (bCombo !== aCombo) return bCombo - aCombo;
            if (b.maxStackCount !== a.maxStackCount) return b.maxStackCount - a.maxStackCount;
            if (b.holeNumber !== a.holeNumber) return b.holeNumber - a.holeNumber;
            const aOver = a.parIsValid && a.holePar !== null ? a.grossScore - a.holePar : a.grossScore;
            const bOver = b.parIsValid && b.holePar !== null ? b.grossScore - b.holePar : b.grossScore;
            return bOver - aOver;
          });

          const best = cabCandidates[0];

          let scoreLabel: string;
          if (best.parIsValid && best.holePar !== null) {
            const over = best.grossScore - best.holePar;
            if (over === 2) scoreLabel = 'double bogey';
            else if (over === 3) scoreLabel = 'triple bogey';
            else scoreLabel = `a ${best.grossScore}`;
          } else {
            scoreLabel = `a ${best.grossScore}`;
          }

          const closingLeg = best.holeNumber === 9 ? 'front' : 'back';
          const handedLeg = best.holeNumber === 9 ? 'front' : 'back and overall';

          let cabText: string;
          if (best.nassauLeg && best.pressRunning) {
            cabText = `${best.subjectName} made ${scoreLabel} on hole ${best.holeNumber} — ${best.opponentName} wins the hole, the press, and closes the ${closingLeg}.`;
          } else if (best.nassauLeg) {
            cabText = `${best.subjectName} made ${scoreLabel} on hole ${best.holeNumber} — hands ${best.opponentName} the ${handedLeg}.`;
          } else if (best.pressRunning) {
            cabText = `${best.subjectName} made ${scoreLabel} on hole ${best.holeNumber} with a press on the line. ${best.opponentName} wins the hole and collects the press.`;
          } else {
            cabText = `${best.subjectName} made ${scoreLabel} on hole ${best.holeNumber} — swung the match by 2 holes in ${best.opponentName}'s favor.`;
          }

          const margin = best.parIsValid && best.holePar !== null
            ? best.grossScore - best.holePar
            : best.grossScore;

          candidates.push({
            type: 'crash_and_burn',
            priority: 1,
            text: cabText,
            holeNumber: best.holeNumber,
            subjectName: best.subjectName,
            opponentName: best.opponentName,
            teamName: null,
            dataPoints: [
              `hole ${best.holeNumber} subject=${best.subjectName}`,
              `grossScore=${best.grossScore}${best.holePar !== null ? ` par=${best.holePar}` : ''}`,
              ...(best.pressRunning ? [`press running stackCount=${best.maxStackCount}`] : []),
              `hole result=${cabHoleResultMap[best.holeNumber] === 'A' ? 'A wins' : 'B wins'}`,
              `statusSwing=${best.statusSwing}`,
            ],
            margin,
          });
        }
      }
    }

    // ─── Gross Heroics (priority 2) ───────────────────────────────────────────
    let statusMap: Map<number, number> | null = null;
    try {
      if (completeHoles.length > 0) {
        const startH = Math.min(...completeHoles);
        const endH = Math.max(...completeHoles);
        statusMap = getRunningStatus(input, startH, endH);
      }
    } catch {
      statusMap = null;
    }

    for (const player of allPlayers) {
      const opponentPlayers = teamAPlayerIds.has(player.id)
        ? allPlayers.filter(p => teamBPlayerIds.has(p.id))
        : allPlayers.filter(p => teamAPlayerIds.has(p.id));

      for (const holeNum of completeHoles) {
        const myScore = input.scores.find(s => s.playerId === player.id && s.holeNumber === holeNum);
        if (!myScore || myScore.receivesStroke !== false || myScore.grossScore === null) continue;

        const oppScores = opponentPlayers.map(op => ({
          name: op.name,
          net: input.scores.find(s => s.playerId === op.id && s.holeNumber === holeNum)?.netScore ?? null,
        }));
        if (oppScores.some(o => o.net === null)) continue;
        const oppScoresSafe = oppScores as { name: string; net: number }[];

        const oppBestNet = Math.min(...oppScoresSafe.map(o => o.net));
        if (myScore.grossScore >= oppBestNet) continue;

        const margin = oppBestNet - myScore.grossScore;

        const holePar = input.holes.find(h => h.number === holeNum)?.par ?? null;
        const parIsValid = holePar !== null && holePar >= 3 && holePar <= 5;
        const isEagle = parIsValid && holePar !== null && myScore.grossScore <= holePar - 2;
        const isBirdie = parIsValid && !isEagle && holePar !== null && myScore.grossScore <= holePar - 1;

        const isHoleInOne = parIsValid && holePar === 3 && myScore.grossScore === 1;
        if (isHoleInOne) {
          const pressRunning = input.presses.some(p => p.startingHole <= holeNum);
          const oppTeamName = opponentPlayers.map(p => p.name).join(' & ');
          const is2v2 = input.matchFormat === '2v2' && opponentPlayers.length === 2;

          let hioText: string;
          if (pressRunning) {
            hioText = `${player.name} aced hole ${holeNum} with a press on the line. Gross 1 on a par 3. Match over.`;
          } else if (is2v2 && oppTeamName) {
            hioText = `${player.name} aced hole ${holeNum}! Gross 1 on a par 3. ${oppTeamName} never had a chance.`;
          } else {
            hioText = `${player.name} aced hole ${holeNum}! Gross 1 on a par 3.`;
          }

          candidates.push({
            type: 'hole_in_one',
            priority: 0,
            text: hioText,
            holeNumber: holeNum,
            subjectName: player.name,
            opponentName: opponentPlayers.length > 0 ? opponentPlayers[0].name : null,
            teamName: is2v2 ? oppTeamName : null,
            dataPoints: [
              `${player.name} grossScore=1 hole${holeNum}`,
              `holePar=${holePar}`,
            ],
            margin: 0,
          });
          continue;
        }

        const signal1 = isEagle || isBirdie;
        const signal2 = input.presses.some(p => p.startingHole <= holeNum);

        let signal3 = false;
        if (statusMap !== null) {
          const statusBefore = statusMap.get(holeNum - 1);
          const statusAfter = statusMap.get(holeNum);
          if (statusBefore !== undefined && statusAfter !== undefined) {
            signal3 = statusBefore !== statusAfter;
          }
        }

        const signal4 =
          holeNum === 9 ||
          holeNum === 18 ||
          holeNum === input.startingHole + input.holes.length - 1;

        const signal5 = margin >= 2;

        if (!signal1 && !signal2 && !signal3 && !signal4 && !signal5) continue;

        let text: string;
        const beatsAll = oppScoresSafe.every(o => myScore.grossScore! < o.net);
        const namedOpp = oppScoresSafe.reduce((a, b) => a.net < b.net ? a : b.net < a.net ? b : a.name > b.name ? b : a);
        const is2v2Both = input.matchFormat === '2v2' && oppScoresSafe.length === 2 && beatsAll;

        if (signal1 && signal2 && signal4) {
          const verb = isEagle ? 'eagled' : 'birdied';
          if (is2v2Both) {
            text = `${player.name} ${verb} hole ${holeNum} with a gross ${myScore.grossScore} — beat both ${oppScoresSafe[0].name}'s net ${oppScoresSafe[0].net} and ${oppScoresSafe[1].name}'s net ${oppScoresSafe[1].net} on the closing hole. Press decided.`;
          } else {
            text = `${player.name} ${verb} hole ${holeNum} with a gross ${myScore.grossScore} — beat ${namedOpp.name}'s net ${namedOpp.net} on the closing hole. Press decided.`;
          }
        } else if (signal2 && signal4) {
          if (is2v2Both) {
            text = `${player.name}'s gross ${myScore.grossScore} on hole ${holeNum} beat both ${oppScoresSafe[0].name}'s net ${oppScoresSafe[0].net} and ${oppScoresSafe[1].name}'s net ${oppScoresSafe[1].net} on the closing hole — press decided, no stroke needed.`;
          } else {
            text = `${player.name}'s gross ${myScore.grossScore} on hole ${holeNum} beat ${namedOpp.name}'s net ${namedOpp.net} on the closing hole — press decided, no stroke needed.`;
          }
        } else if (signal2) {
          if (signal1) {
            const verb = isEagle ? 'eagled' : 'birdied';
            if (is2v2Both) {
              text = `${player.name} ${verb} hole ${holeNum} with a gross ${myScore.grossScore} — beat both ${oppScoresSafe[0].name}'s net ${oppScoresSafe[0].net} and ${oppScoresSafe[1].name}'s net ${oppScoresSafe[1].net} with a press on the line. No stroke needed.`;
            } else {
              text = `${player.name} ${verb} hole ${holeNum} with a gross ${myScore.grossScore} — beat ${namedOpp.name}'s net ${namedOpp.net} with a press on the line. No stroke needed.`;
            }
          } else {
            if (is2v2Both) {
              text = `${player.name}'s gross ${myScore.grossScore} on hole ${holeNum} beat both ${oppScoresSafe[0].name}'s net ${oppScoresSafe[0].net} and ${oppScoresSafe[1].name}'s net ${oppScoresSafe[1].net} with a press on the line — no stroke needed.`;
            } else {
              text = `${player.name}'s gross ${myScore.grossScore} on hole ${holeNum} beat ${namedOpp.name}'s net ${namedOpp.net} with a press on the line — no stroke needed.`;
            }
          }
        } else {
          if (isEagle || isBirdie) {
            const verb = isEagle ? 'eagled' : 'birdied';
            if (is2v2Both) {
              text = `${player.name} ${verb} hole ${holeNum} with a gross ${myScore.grossScore} — beat both ${oppScoresSafe[0].name}'s net ${oppScoresSafe[0].net} and ${oppScoresSafe[1].name}'s net ${oppScoresSafe[1].net}. No stroke needed.`;
            } else {
              text = `${player.name} ${verb} hole ${holeNum} with a gross ${myScore.grossScore} — beat ${namedOpp.name}'s net ${namedOpp.net}. No stroke needed.`;
            }
          } else {
            if (is2v2Both) {
              text = `${player.name}'s gross ${myScore.grossScore} on hole ${holeNum} beat both ${oppScoresSafe[0].name}'s net ${oppScoresSafe[0].net} and ${oppScoresSafe[1].name}'s net ${oppScoresSafe[1].net} — no stroke needed.`;
            } else {
              text = `${player.name}'s gross ${myScore.grossScore} on hole ${holeNum} beat ${namedOpp.name}'s net ${namedOpp.net} — no stroke needed.`;
            }
          }
        }

        candidates.push({
          type: 'gross_heroics',
          priority: 2,
          text,
          holeNumber: holeNum,
          subjectName: player.name,
          opponentName: oppScoresSafe.reduce((a, b) => a.net < b.net ? a : b).name,
          teamName: null,
          dataPoints: [
            `${player.name} grossScore=${myScore.grossScore} hole${holeNum}`,
            ...oppScoresSafe.map(o => `${o.name} netScore=${o.net} hole${holeNum}`),
          ],
          margin,
        });
      }
    }

    // ─── Handicap Injustice (priority 3) ─────────────────────────────────────
    const totalForA = input.totalAmountForA;
    if (Number.isFinite(totalForA) && totalForA !== 0) {
      const teamAWon = totalForA > 0;
      const teamAPlayers = input.teams[0]?.players ?? [];
      const teamBPlayers = input.teams[1]?.players ?? [];
      const winningTeamPlayers = teamAWon ? teamAPlayers : teamBPlayers;
      const losingTeamPlayers = teamAWon ? teamBPlayers : teamAPlayers;
      const winningTeamName = winningTeamPlayers.map(p => p.name).join(' & ') || 'Winning Team';
      const losingTeamName = losingTeamPlayers.map(p => p.name).join(' & ') || 'Losing Team';

      let injusticeCard: RICard | null = null;

      for (const loser of losingTeamPlayers) {
        if (loser.strokesReceived < 2) continue;
        const lessStrokedWinner = winningTeamPlayers.find(
          w => w.strokesReceived < loser.strokesReceived
        );
        if (!lessStrokedWinner) continue;

        let text: string;
        if (input.matchFormat === '1v1') {
          const winner = winningTeamPlayers[0];
          text = `${loser.name} received ${loser.strokesReceived} strokes today and still lost the cash to ${winner.name}.`;
        } else {
          text = `${loser.name} received ${loser.strokesReceived} strokes today. ${lessStrokedWinner.name} played straight up and ${winningTeamName} still won the money.`;
        }

        injusticeCard = {
          type: 'handicap_injustice',
          priority: 3,
          text,
          holeNumber: null,
          subjectName: loser.name,
          opponentName: lessStrokedWinner.name,
          teamName: winningTeamName,
          dataPoints: [
            `${loser.name} strokesReceived=${loser.strokesReceived}`,
            `${loser.name} netAmount<0 (lost)`,
            `${lessStrokedWinner.name} strokesReceived=${lessStrokedWinner.strokesReceived}`,
          ],
          margin: 0,
        };
        break;
      }

      if (!injusticeCard) {
        for (const winner of winningTeamPlayers) {
          if (winner.strokesReceived !== 0) continue;
          const highStrokeLoser = losingTeamPlayers.reduce<RIPlayer | null>(
            (best, l) => l.strokesReceived >= 3 && (best === null || l.strokesReceived > best.strokesReceived) ? l : best,
            null
          );
          if (!highStrokeLoser) continue;

          let text: string;
          if (input.matchFormat === '1v1') {
            const loser = losingTeamPlayers[0];
            text = `${winner.name} played all 18 holes with no strokes and still took the cash from ${loser.name}.`;
          } else {
            text = `${winner.name} played straight up all day — no strokes — and ${losingTeamName} still couldn't beat ${winningTeamName}.`;
          }

          injusticeCard = {
            type: 'handicap_injustice',
            priority: 3,
            text,
            holeNumber: null,
            subjectName: winner.name,
            opponentName: highStrokeLoser.name,
            teamName: losingTeamName,
            dataPoints: [
              `${winner.name} strokesReceived=0`,
              `${winner.name} netAmount>0 (won)`,
              `${highStrokeLoser.name} strokesReceived=${highStrokeLoser.strokesReceived}`,
            ],
            margin: 0,
          };
          break;
        }
      }

      if (injusticeCard) candidates.push(injusticeCard);
    }

    // ─── Stroke Pressure (priority 3) ────────────────────────────────────────
    {
      type StrokePressureCandidate = {
        variant: 1 | 2;
        holeNumber: number;
        subjectName: string;
        opponentName: string;
        playerANet: number;
        opponentBestGross: number;
        holePar: number;
      };

      const spCandidates: StrokePressureCandidate[] = [];

      for (const player of allPlayers) {
        const opponentPlayers = teamAPlayerIds.has(player.id)
          ? allPlayers.filter(p => teamBPlayerIds.has(p.id))
          : allPlayers.filter(p => teamAPlayerIds.has(p.id));

        for (const holeNum of completeHoles) {
          const myScore = input.scores.find(
            s => s.playerId === player.id && s.holeNumber === holeNum
          );
          if (!myScore || myScore.receivesStroke !== true) continue;
          if (myScore.netScore === null) continue;

          const holePar = input.holes.find(h => h.number === holeNum)?.par ?? null;
          if (holePar === null) continue;
          const parIsValid = holePar >= 3 && holePar <= 5;
          if (!parIsValid) continue;

          if (myScore.netScore !== holePar - 1) continue;

          const oppGrossEntries: { name: string; gross: number }[] = [];
          let anyOppNull = false;
          for (const opp of opponentPlayers) {
            const oppScore = input.scores.find(
              s => s.playerId === opp.id && s.holeNumber === holeNum
            );
            if (!oppScore || oppScore.grossScore === null) { anyOppNull = true; break; }
            oppGrossEntries.push({ name: opp.name, gross: oppScore.grossScore });
          }
          if (anyOppNull || oppGrossEntries.length === 0) continue;

          const opponentBestGross = Math.min(...oppGrossEntries.map(e => e.gross));

          if (opponentBestGross > holePar - 1) continue;
          if (opponentBestGross > myScore.netScore) continue;

          const variant: 1 | 2 = opponentBestGross === myScore.netScore ? 1 : 2;
          const namedOpp = oppGrossEntries.find(e => e.gross === opponentBestGross)!;

          spCandidates.push({
            variant,
            holeNumber: holeNum,
            subjectName: player.name,
            opponentName: namedOpp.name,
            playerANet: myScore.netScore,
            opponentBestGross,
            holePar,
          });
        }
      }

      if (spCandidates.length > 0) {
        spCandidates.sort((a, b) =>
          b.variant !== a.variant
            ? b.variant - a.variant
            : b.holeNumber !== a.holeNumber
              ? b.holeNumber - a.holeNumber
              : a.subjectName.localeCompare(b.subjectName)
        );
        const best = spCandidates[0];

        const isOppEagle = best.opponentBestGross <= best.holePar - 2;
        const oppVerb = isOppEagle ? 'eagled' : 'birdied';

        let text: string;
        if (best.variant === 1) {
          if (input.matchFormat === '1v1') {
            text = `${best.subjectName}'s stroke on hole ${best.holeNumber} gave ${best.subjectName} net ${best.playerANet}. ${best.opponentName} had to ${oppVerb === 'eagled' ? 'eagle' : 'birdie'} for real just to halve it — made gross ${best.opponentBestGross}. Hole tied.`;
          } else {
            text = `${best.subjectName}'s stroke on hole ${best.holeNumber} gave ${best.subjectName} net ${best.playerANet}. ${best.opponentName} had to ${oppVerb === 'eagled' ? 'eagle' : 'birdie'} for real to halve it — made gross ${best.opponentBestGross}. Hole tied.`;
          }
        } else {
          if (input.matchFormat === '1v1') {
            text = `${best.opponentName} ${oppVerb} hole ${best.holeNumber} with a gross ${best.opponentBestGross} — and still lost to ${best.subjectName}'s net ${best.playerANet}. The stroke made the difference.`;
          } else {
            text = `${best.opponentName} ${oppVerb} hole ${best.holeNumber} with a gross ${best.opponentBestGross} — still lost to ${best.subjectName}'s net ${best.playerANet} even with the stroke.`;
          }
        }

        candidates.push({
          type: 'stroke_pressure',
          priority: 3,
          text,
          holeNumber: best.holeNumber,
          subjectName: best.subjectName,
          opponentName: best.opponentName,
          teamName: null,
          dataPoints: [
            `${best.subjectName} netScore=${best.playerANet} hole${best.holeNumber} par${best.holePar}`,
            `${best.opponentName} grossScore=${best.opponentBestGross} hole${best.holeNumber}`,
          ],
          margin: best.variant,
        });
      }
    }

    // ─── Press Moment (priority 2B) ───────────────────────────────────────────
    if (input.presses.length > 0) {
      let holeResultMap: Record<number, 'A' | 'B' | 'Tie'> = {};
      try {
        holeResultMap = buildHoleResults(input);
      } catch {
        holeResultMap = {};
      }

      const grossHeroicHoles = new Set(
        candidates
          .filter(c => c.type === 'gross_heroics')
          .map(c => c.holeNumber)
          .filter((h): h is number => h !== null)
      );

      type PressMomentCandidate = {
        holeNum: number;
        winnerTeamIdx: number;
        isClosing: boolean;
        maxStackCount: number;
      };

      const pressMomentHoles: PressMomentCandidate[] = [];

      for (const holeNum of completeHoles) {
        if (grossHeroicHoles.has(holeNum)) continue;

        const runningPresses = input.presses.filter(p => p.startingHole <= holeNum);
        if (runningPresses.length === 0) continue;

        const holeResult = holeResultMap[holeNum];
        if (!holeResult || holeResult === 'Tie') continue;

        const winnerTeamIdx = holeResult === 'A' ? 0 : 1;
        const winnerTeam = input.teams[winnerTeamIdx];
        if (!winnerTeam) continue;

        const hasScore = winnerTeam.players.some(p => {
          const s = input.scores.find(sc => sc.playerId === p.id && sc.holeNumber === holeNum);
          return s && s.grossScore !== null;
        });
        if (!hasScore) continue;

        pressMomentHoles.push({
          holeNum,
          winnerTeamIdx,
          isClosing: holeNum === 9 || holeNum === 18,
          maxStackCount: Math.max(...runningPresses.map(p => p.stackCount)),
        });
      }

      if (pressMomentHoles.length > 0) {
        pressMomentHoles.sort((a, b) =>
          (b.isClosing ? 1 : 0) - (a.isClosing ? 1 : 0) ||
          b.maxStackCount - a.maxStackCount ||
          b.holeNum - a.holeNum
        );

        const best = pressMomentHoles[0];
        const { holeNum, winnerTeamIdx, isClosing, maxStackCount } = best;
        const winnerTeam = input.teams[winnerTeamIdx];
        const loserTeam = input.teams[winnerTeamIdx === 0 ? 1 : 0];
        const holePar = input.holes.find(h => h.number === holeNum)?.par ?? null;
        const parIsValid = holePar !== null && holePar >= 3 && holePar <= 5;
        const is2v2 = input.matchFormat === '2v2';

        let pmText: string;
        let pmSubject: string | null = null;
        let pmOpponent: string | null = null;
        let pmTeamName: string | null = null;

        if (is2v2) {
          const scoredWinners = winnerTeam.players
            .map(p => ({
              player: p,
              net: input.scores.find(s => s.playerId === p.id && s.holeNumber === holeNum)?.netScore ?? null,
              gross: input.scores.find(s => s.playerId === p.id && s.holeNumber === holeNum)?.grossScore ?? null,
            }))
            .filter((x): x is { player: RIPlayer; net: number; gross: number | null } =>
              x.net !== null
            );

          pmTeamName = winnerTeam.players.map(p => p.name).join(' & ');

          if (scoredWinners.length > 0) {
            const carrier = scoredWinners.reduce((a, b) => a.net <= b.net ? a : b);
            pmSubject = carrier.player.name;
            const cGross = carrier.gross;
            pmText = cGross !== null
              ? `${pmSubject} carried hole ${holeNum} for ${pmTeamName} with a gross ${cGross} — press on the line. Best score on the hole.`
              : `${pmTeamName} won hole ${holeNum} with a press on the line.`;
          } else {
            pmText = `${pmTeamName} won hole ${holeNum} with a press on the line.`;
          }
        } else {
          const winner = winnerTeam.players[0];
          const opponent = loserTeam?.players[0];
          pmSubject = winner?.name ?? null;
          pmOpponent = opponent?.name ?? null;

          if (!winner) {
            pmText = `A player won hole ${holeNum} with a press on the line.`;
          } else {
            const winnerScore = input.scores.find(s => s.playerId === winner.id && s.holeNumber === holeNum);
            const opponentScore = opponent
              ? input.scores.find(s => s.playerId === opponent.id && s.holeNumber === holeNum)
              : null;
            const winnerGross = winnerScore?.grossScore ?? null;
            const opponentGross = opponentScore?.grossScore ?? null;

            if (winnerGross === null) {
              pmText = `${winner.name}'s team won hole ${holeNum} with a press on the line.`;
            } else {
              const isBogeyOrWorse = parIsValid && holePar !== null && winnerGross >= holePar + 1;

              if (isClosing) {
                pmText = opponentGross !== null
                  ? `${winner.name} won hole ${holeNum} with a gross ${winnerGross} — closed the press on the closing hole. ${pmOpponent ?? 'Opponent'} needed better than ${opponentGross}.`
                  : `${winner.name} won hole ${holeNum} with a gross ${winnerGross} — closed the press on the closing hole.`;
              } else if (isBogeyOrWorse) {
                pmText = opponentGross !== null
                  ? `${winner.name} made a gross ${winnerGross} on hole ${holeNum} — good enough to win the press. ${pmOpponent ?? 'Opponent'} made ${opponentGross}.`
                  : `${winner.name} made a gross ${winnerGross} on hole ${holeNum} — good enough to win the press.`;
              } else {
                pmText = opponentGross !== null
                  ? `${winner.name} won hole ${holeNum} with a gross ${winnerGross} — beat ${pmOpponent ?? 'opponent'}'s gross ${opponentGross} with a press on the line.`
                  : `${winner.name} won hole ${holeNum} with a gross ${winnerGross} with a press on the line.`;
              }
            }
          }
        }

        candidates.push({
          type: 'press_moment',
          priority: 2,
          text: pmText,
          holeNumber: holeNum,
          subjectName: pmSubject,
          opponentName: pmOpponent,
          teamName: pmTeamName,
          stackCount: maxStackCount,
          dataPoints: [
            `hole ${holeNum} winner=${winnerTeamIdx === 0 ? 'A' : 'B'}`,
            ...(pmSubject ? [`${pmSubject} grossScore on hole ${holeNum}`] : []),
            ...(pmOpponent ? [`opponent=${pmOpponent}`] : []),
            ...input.presses
              .filter(p => p.startingHole <= holeNum)
              .map(p => `press startingHole=${p.startingHole} stack=${p.stackCount}`),
          ],
          margin: 0,
        });
      }
    }

    // ─── Press Intelligence (priority 4) ─────────────────────────────────────
    {
      const presses = input.presses;
      const nassauAmountForA = input.nassauAmountForA ?? null;
      const pressAmountForA = input.pressAmountForA ?? null;

      if (
        presses.length > 0 &&
        totalForA !== 0 &&
        nassauAmountForA !== null &&
        pressAmountForA !== null
      ) {
        try {
          const winnerIsA = totalForA > 0;
          const winnerTotal = Math.abs(totalForA);

          const teamALabel = input.matchFormat === '1v1'
            ? (input.teams[0]?.players[0]?.name ?? 'Team A')
            : (input.teams[0]?.players.map(p => p.name).join(' & ') || 'Team A');
          const teamBLabel = input.matchFormat === '1v1'
            ? (input.teams[1]?.players[0]?.name ?? 'Team B')
            : (input.teams[1]?.players.map(p => p.name).join(' & ') || 'Team B');
          const winnerLabel = winnerIsA ? teamALabel : teamBLabel;
          const loserLabel  = winnerIsA ? teamBLabel : teamALabel;

          const winnerNassau = winnerIsA ? nassauAmountForA : -nassauAmountForA;
          const winnerPress  = winnerIsA ? pressAmountForA  : -pressAmountForA;

          let piCard: RICard | null = null;

          if (winnerNassau < 0 && winnerPress > 0 && winnerTotal >= 5) {
            const nassauLoss = Math.round(Math.abs(winnerNassau) * 100) / 100;
            const pressWon   = Math.round(winnerPress * 100) / 100;
            const flipAmt    = Math.round(winnerTotal * 100) / 100;
            const text =
              `${loserLabel} won the Nassau by $${nassauLoss} — but ${winnerLabel}'s presses cancelled it out and flipped the match by $${flipAmt}.`;
            piCard = {
              type: 'press_intelligence',
              priority: 4,
              text,
              holeNumber: null,
              subjectName: winnerLabel,
              opponentName: loserLabel,
              teamName: null,
              dataPoints: [
                `pressTotal=${pressWon}`,
                `nassauTotal=-${nassauLoss} (loser won Nassau)`,
                `flipAmount=${flipAmt}`,
                `${winnerLabel} won presses`,
                `${loserLabel} won Nassau`,
              ],
              margin: flipAmt,
              pressTotal: pressWon,
              nassauTotal: nassauLoss,
            };
          }

          if (!piCard && winnerPress > 0 && winnerPress >= winnerNassau + 5) {
            const nassauAmt = Math.round(winnerNassau * 100) / 100;
            const pressAmt  = Math.round(winnerPress * 100) / 100;
            let text: string;
            if (nassauAmt === 0) {
              text = `${winnerLabel} collected $${pressAmt} in presses against ${loserLabel}. The Nassau itself? A push.`;
            } else if (nassauAmt < 0) {
              text = `The presses were worth more than the entire Nassau today — ${winnerLabel} made $${pressAmt} on presses. ${loserLabel} won the Nassau but lost the match.`;
            } else {
              text = `${winnerLabel} swept the Nassau and added $${pressAmt} in presses. ${loserLabel} never had a chance.`;
            }
            piCard = {
              type: 'press_intelligence',
              priority: 4,
              text,
              holeNumber: null,
              subjectName: winnerLabel,
              opponentName: loserLabel,
              teamName: null,
              dataPoints: [
                `pressTotal=${pressAmt}`,
                `nassauTotal=${nassauAmt}`,
                `${winnerLabel} won presses`,
              ],
              margin: pressAmt - nassauAmt,
              pressTotal: pressAmt,
              nassauTotal: nassauAmt,
            };
          }

          if (piCard) candidates.push(piCard);
        } catch {
          // Fail safe — skip on any computation error
        }
      }
    }

    // ─── Teammate Dynamic (priority 5) ───────────────────────────────────────
    if (input.matchFormat === '2v2') {
      try {
        const holeResultMap = buildHoleResults(input);
        const teamAPlayers = input.teams[0]?.players ?? [];
        const teamBPlayers = input.teams[1]?.players ?? [];
        const a0 = teamAPlayers[0];
        const a1 = teamAPlayers[1];
        const b0 = teamBPlayers[0];
        const b1 = teamBPlayers[1];

        if (a0 && a1 && b0 && b1) {
          const computeWins = (
            player: RIPlayer,
            partner: RIPlayer,
            teamSide: 'A' | 'B',
            oppPlayers: RIPlayer[],
          ): number | null => {
            let wins = 0;
            for (const holeNum of completeHoles) {
              const result = holeResultMap[holeNum];
              if (result !== teamSide) continue;
              const ps = input.scores.find(s => s.playerId === player.id && s.holeNumber === holeNum);
              const ts = input.scores.find(s => s.playerId === partner.id && s.holeNumber === holeNum);
              if (!ps || ps.netScore === null || !ts || ts.netScore === null) return null;
              if (ps.netScore >= ts.netScore) continue;
              const oppNets: number[] = [];
              for (const opp of oppPlayers) {
                const os = input.scores.find(s => s.playerId === opp.id && s.holeNumber === holeNum);
                if (!os || os.netScore === null) return null;
                oppNets.push(os.netScore);
              }
              const opponentBestNet = Math.min(...oppNets);
              if (ps.netScore < opponentBestNet) wins++;
            }
            return wins;
          };

          const a0Wins = computeWins(a0, a1, 'A', [b0, b1]);
          const a1Wins = computeWins(a1, a0, 'A', [b0, b1]);
          const b0Wins = computeWins(b0, b1, 'B', [a0, a1]);
          const b1Wins = computeWins(b1, b0, 'B', [a0, a1]);

          if (a0Wins !== null && a1Wins !== null && b0Wins !== null && b1Wins !== null) {
            const gapA = Math.abs(a0Wins - a1Wins);
            const gapB = Math.abs(b0Wins - b1Wins);

            const teamAWon = totalForA > 0;
            const teamBWon = totalForA < 0;

            let featuredSide: 'A' | 'B' | null = null;
            if (gapA >= 2 && gapB >= 2) {
              if (gapA > gapB) featuredSide = 'A';
              else if (gapB > gapA) featuredSide = 'B';
              else featuredSide = teamAWon ? 'A' : teamBWon ? 'B' : 'A';
            } else if (gapA >= 2) {
              featuredSide = 'A';
            } else if (gapB >= 2) {
              featuredSide = 'B';
            }

            if (featuredSide) {
              const p0 = featuredSide === 'A' ? a0 : b0;
              const p1 = featuredSide === 'A' ? a1 : b1;
              const p0Wins = featuredSide === 'A' ? a0Wins : b0Wins;
              const p1Wins = featuredSide === 'A' ? a1Wins : b1Wins;
              const featuredTeamWon = featuredSide === 'A' ? teamAWon : teamBWon;
              const isTie = totalForA === 0;

              const carrierIsP0 = p0Wins >= p1Wins;
              const carrier = carrierIsP0 ? p0 : p1;
              const partner = carrierIsP0 ? p1 : p0;
              const carrierWins = carrierIsP0 ? p0Wins : p1Wins;
              const partnerWins = carrierIsP0 ? p1Wins : p0Wins;

              const teamALabel = `${a0.name} & ${a1.name}`;
              const teamBLabel = `${b0.name} & ${b1.name}`;
              const featuredTeamLabel = featuredSide === 'A' ? teamALabel : teamBLabel;
              const oppTeamLabel      = featuredSide === 'A' ? teamBLabel : teamALabel;

              let tdText: string;
              const useVariant1 = featuredTeamWon && !isTie;

              if (useVariant1) {
                if (partnerWins === 0) {
                  tdText = `${carrier.name} carried ${featuredTeamLabel} — personally winning ${carrierWins} holes. ${partner.name} won zero holes outright.`;
                } else {
                  tdText = `${carrier.name} personally won ${carrierWins} holes for ${featuredTeamLabel}. ${partner.name} contributed ${partnerWins}. ${carrier.name} was the difference.`;
                }
              } else {
                if (partnerWins === 0) {
                  tdText = `${carrier.name} won ${carrierWins} holes for ${featuredTeamLabel} — ${partner.name} won zero. ${carrier.name} couldn't do it alone.`;
                } else {
                  tdText = `${carrier.name} won ${carrierWins} holes for ${featuredTeamLabel}. ${partner.name} won ${partnerWins}. It wasn't enough against ${oppTeamLabel}.`;
                }
              }

              candidates.push({
                type: 'teammate_dynamic',
                priority: 5,
                text: tdText,
                holeNumber: null,
                subjectName: carrier.name,
                opponentName: partner.name,
                teamName: featuredTeamLabel,
                dataPoints: [
                  `${carrier.name} personalHoleWins=${carrierWins}`,
                  `${partner.name} personalHoleWins=${partnerWins}`,
                  `gap=${carrierWins - partnerWins} team=${featuredSide}${featuredTeamWon ? ' wins' : isTie ? ' tie' : ' loses'}`,
                ],
                margin: carrierWins - partnerWins,
                carrierName: carrier.name,
                partnerName: partner.name,
                carrierHoleWins: carrierWins,
                partnerHoleWins: partnerWins,
              });
            }
          }
        }
      } catch {
        // Fail safe — skip on any computation error
      }
    }

    // ─── Selection ────────────────────────────────────────────────────────────
    const hioCard = candidates.find(c => c.type === 'hole_in_one') ?? null;
    const rankedPool = candidates
      .filter(c => c.type !== 'hole_in_one')
      .sort((a, b) =>
        a.priority !== b.priority
          ? a.priority - b.priority
          : b.margin !== a.margin
            ? b.margin - a.margin
            : (b.holeNumber ?? 0) !== (a.holeNumber ?? 0)
              ? (b.holeNumber ?? 0) - (a.holeNumber ?? 0)
              : (a.subjectName ?? '').localeCompare(b.subjectName ?? '')
      );

    const remainingSlots = hioCard ? maxCards - 1 : maxCards;
    const displayCards: RICard[] = hioCard
      ? [hioCard, ...rankedPool.slice(0, remainingSlots)]
      : rankedPool.slice(0, maxCards);

    return displayCards;
  } catch (e) {
    console.error('RI error:', e);
    return [];
  }
}

// ─── React Renderer ───────────────────────────────────────────────────────────

export function RoundIntelligenceSection({ cards }: { cards: RICard[] }) {
  if (cards.length === 0) return null;
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-testid="round-intelligence-section">
      <div className="bg-gray-800 text-white px-4 py-2">
        <h2 className="font-bold text-sm uppercase tracking-wide">Round Intelligence</h2>
      </div>
      <div className="px-4 py-3 space-y-3">
        {cards.map((card, idx) => {
          if (card.type === 'hole_in_one') {
            return (
              <div
                key={`hole_in_one-${card.holeNumber}-${card.subjectName}`}
                className="bg-[#064E3B] rounded-lg px-3 py-3"
                data-testid="round-intelligence-card-hole-in-one"
              >
                <p className="text-[#FACC15] font-bold text-sm mb-1">⛳ HOLE IN ONE</p>
                <p className="text-white text-sm leading-snug">{card.text}</p>
              </div>
            );
          }
          return (
            <div
              key={`${card.type}-${card.holeNumber ?? 'none'}-${card.subjectName ?? idx}`}
              className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-3"
              data-testid={`round-intelligence-card-${idx}`}
            >
              <p className="text-sm text-gray-800 leading-snug">{card.text}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
